import * as React from "react"
import { Check, ChevronsUpDown, Loader2, Trash2, X } from "lucide-react"

import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { cn } from "../../lib/utils"
import { useUILabels } from "./ui-labels"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"

export interface MultiPickOption {
  label: string
  value: string
  icon?: React.ReactNode
  trailing?: React.ReactNode
}

/** Labels for MultiPick / SinglePick. Use {{value}} in createItemLabel for the new value. */
export interface MultiPickLabels {
  placeholder?: string
  emptyMessage?: string
  itemsLabel?: string
  searchPlaceholder?: string
  createItemLabel?: string
  clearSearchTooltip?: string
  clearSelectionsTooltip?: string
}

function formatCreateItemLabel(template: string, value: string): string {
  return template.replace(/\{\{\s*value\s*\}\}/g, () => value)
}

/** Substring-only filter: item matches when its value contains the search (case-insensitive). Avoids fuzzy matches like "imnn" → "Admin". */
function substringFilter(value: string, search: string): number {
  if (!search.trim()) return 1
  return value.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0
}

export interface MultiPickProps {
  options: MultiPickOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  singlePick?: boolean
  pickIfOne?: boolean
  disabled?: boolean
  className?: string
  allowNewItem?: boolean
  listClassName?: string
  searchable?: boolean
  /** Called when the popover opens or closes. */
  onOpenChange?: (open: boolean) => void
  /** Called when the search input value changes. */
  onSearchChange?: (query: string) => void
  /** Custom trigger element rendered instead of the default button. Receives `open` and `selected` state. */
  customTrigger?: (state: { open: boolean; selected: string[] }) => React.ReactNode
  /** Optional labels configuration. */
  labels?: Partial<MultiPickLabels>
  /** Show a loading indicator at the bottom of the list. */
  loading?: boolean
  /** When true, do not filter options client-side based on the search query. */
  disableClientFilter?: boolean
  /** Hide the "clear selections" (trash) button — e.g. when an explicit "All" option already resets. */
  hideClearSelections?: boolean
  /** Max char length of the concatenated selected labels in the trigger before falling back to "N items" summary. Default 30. */
  concatLabelsMaxLength?: number
}

export function MultiPick({
  options,
  selected,
  onChange,
  singlePick,
  pickIfOne = false,
  disabled = false,
  className,
  allowNewItem = false,
  searchable = true,
  listClassName = "w-[var(--radix-popover-trigger-width)]",
  disableClientFilter = false,
  hideClearSelections = false,
  concatLabelsMaxLength = 30,
  onOpenChange,
  onSearchChange,
  customTrigger,
  labels,
  loading = false,
}: MultiPickProps) {
  const { multiPick } = useUILabels()
  const t = {
    placeholder: labels?.placeholder ?? multiPick.placeholder,
    emptyMessage: labels?.emptyMessage ?? multiPick.emptyMessage,
    itemsLabel: labels?.itemsLabel ?? multiPick.itemsLabel,
    searchPlaceholder: labels?.searchPlaceholder ?? multiPick.searchPlaceholder,
    createItemLabel: labels?.createItemLabel ?? multiPick.createItemLabel,
    clearSearchTooltip:
      labels?.clearSearchTooltip ??
      multiPick.clearSearchTooltip ??
      "Clear Search",
    clearSelectionsTooltip:
      labels?.clearSelectionsTooltip ??
      multiPick.clearSelectionsTooltip ??
      "Clear Selections",
  }

  const [open, setOpenInternal] = React.useState(false)
  const setOpen = React.useCallback((v: boolean) => {
    setOpenInternal(v)
    onOpenChange?.(v)
  }, [onOpenChange])
  const [query, setQueryInternal] = React.useState("")
  const setQuery = React.useCallback((v: string) => {
    setQueryInternal(v)
    onSearchChange?.(v)
  }, [onSearchChange])
  const [createdItems, setCreatedItems] = React.useState<MultiPickOption[]>([])

  const allOptions = React.useMemo(() => {
    const createdMap = new Map(createdItems.map((item) => [item.value, item]))
    const existingMap = new Map(options.map((item) => [item.value, item]))

    // Merge: created items take precedence, then existing options
    const merged = new Map([...createdMap, ...existingMap])
    return Array.from(merged.values())
  }, [options, createdItems])

  React.useEffect(() => {
    if (!pickIfOne) return
    if (allOptions.length !== 1) return
    if (selected.length !== 0) return

    const onlyValue = allOptions[0]?.value
    if (!onlyValue) return

    onChange([onlyValue])
  }, [pickIfOne, allOptions, selected, onChange])

  const handleToggle = (value: string) => {
    if (singlePick) {
      onChange([value])
      setOpen(false)
      return
    }
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const normalizedQuery = query.trim().toLowerCase()
  const hasMatches = disableClientFilter
    ? true
    : normalizedQuery.length === 0
      ? true
      : allOptions.some((o) =>
        o.label.toLowerCase().includes(normalizedQuery)
      )

  const commandFilter = React.useCallback(
    (value: string, search: string) =>
      disableClientFilter ? 1 : substringFilter(value, search),
    [disableClientFilter],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {customTrigger ? (
          customTrigger({ open, selected })
        ) : (
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between hover:bg-transparent hover:text-foreground overflow-hidden", className)}
            disabled={disabled}
          >
            {(() => {
              // Resolve the trigger label as { icon?, text } so the text can live in a single
              // truncating span — long selections ellipsis instead of clipping mid-word.
              const content = (() => {
                if (selected.length === 0) return { text: t.placeholder || '-' }
                if (singlePick) return { text: allOptions.find((o) => o.value === selected[0])?.label ?? selected[0] }
                if (selected.length === 1) {
                  const one = allOptions.find((o) => o.value === selected[0])
                  return { icon: one?.icon, text: one?.label ?? selected[0] }
                }
                const concatenatedLabels = selected.map((s) => allOptions.find((o) => o.value === s)?.label ?? s).join(', ')
                if (concatenatedLabels.length < concatLabelsMaxLength) return { text: concatenatedLabels }
                return { text: `${selected.length} ${t.itemsLabel}` }
              })()
              return (
                <span className="flex min-w-0 items-center gap-2">
                  {content.icon && <span className="flex h-4 w-4 shrink-0 items-center">{content.icon}</span>}
                  <span className="truncate">{content.text}</span>
                </span>
              )
            })()}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[var(--radix-popover-trigger-width)] p-0 rounded-lg", listClassName)}
        align="start"
      >
        <Command className="flex min-h-0 flex-1 flex-col overflow-hidden" filter={commandFilter}>
          {searchable && (
            <div className="relative">
              <CommandInput
                placeholder={t.searchPlaceholder}
                value={query}
                onValueChange={setQuery}
                className="w-full pe-16"
              />
              {query.trim().length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute ltr:right-3 rtl:left-3 top-1/2 h-6 w-6 -translate-y-1/2"
                      onClick={() => {
                        setQuery("")
                      }}
                      aria-label={t.clearSearchTooltip}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t.clearSearchTooltip}
                  </TooltipContent>
                </Tooltip>
              )}
              {selected.length > 0 && !hideClearSelections && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-3 top-1/2 h-6 w-6 -translate-y-1/2"
                      onClick={() => {
                        onChange([])
                        setQuery("")
                      }}
                      aria-label={t.clearSelectionsTooltip}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t.clearSelectionsTooltip}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}
          <CommandList
            className="min-h-0 flex-1 max-h-60 overflow-x-hidden overflow-y-auto custom-scrollbar"
          >
            {!(allowNewItem && normalizedQuery.length > 0 && !hasMatches) && !loading && (
              <CommandEmpty>{t.emptyMessage}</CommandEmpty>
            )}
            <CommandGroup>
              {allowNewItem && query.trim().length > 0 && !hasMatches && (
                <CommandItem
                  key={`__create__${query}`}
                  value={query}
                  onSelect={() => {
                    const newValue = query.trim()
                    onChange(singlePick ? [newValue] : Array.from(new Set([...selected, newValue])))
                    // Add to created items permanently
                    const newItem: MultiPickOption = {
                      label: newValue,
                      value: newValue,
                    }
                    setCreatedItems((prev) => {
                      // Avoid duplicates
                      if (prev.some((item) => item.value === newValue)) {
                        return prev
                      }
                      return [...prev, newItem]
                    })
                    setQuery("")
                    setOpen(false)
                  }}
                  className="cursor-pointer hover:bg-transparent"
                  aria-checked={false}
                >
                  {!singlePick && (
                    <div
                      className={cn(
                        "me-2 flex h-4 w-4 items-center justify-center rounded-sm border border-border opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                  <span>{formatCreateItemLabel(t.createItemLabel, query.trim())}</span>
                </CommandItem>
              )}
              {allOptions.map((option) => {
                const isSelected = selected.includes(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label || option.value}
                    onSelect={() => handleToggle(option.value)}
                    className="cursor-pointer hover:bg-transparent"
                    aria-checked={isSelected}
                  >
                    {!singlePick && (
                      <div
                        className={cn(
                          "me-2 flex h-4 w-4 items-center justify-center rounded-sm border border-border",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                    {option.icon && <span className="me-2 flex h-4 w-4 items-center justify-center">
                      {option.icon}
                    </span>}
                    <span>{option.label}</span>
                    {option.trailing && (
                      <span className="ms-auto flex items-center ps-2">{option.trailing}</span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {loading && (
              <div className="flex items-center justify-center gap-2 border-t border-border px-3 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
